/**
 * PLAT-11213 — the reel's own name: written by the AI when the reel still
 * carries the placeholder it was created with, and typed over by the creator.
 *
 * Sequence, not judgement. Guard → compose → generate → write → hand the new
 * title back so the clip list agrees. What it deliberately does not own is where
 * the title lands in memory (`onRenamed`, because the story lives on a store the
 * host already holds) or what the words are (`chapterizationBaseText`, DS6).
 *
 * The automatic run fires from the clip preview rather than from the three
 * creation paths (DS3): all three return a saved record before there is a
 * timeline to read, and the preview opens straight after a Save as Reel — so
 * from the creator's side this *is* on creation, with one call site instead of
 * three composers. It is what PLAT-11064 did for a Chapter.
 */
import * as EventTracker from '@src/analytics/EventTracker';
import { deriveReelSections } from '@src/services/reel/deriveReelSections';
import {
  normalizeGeneratedReelName,
  reelNameAvoidanceInstruction,
  reelNamesInUse,
} from '@src/services/reel/generatedReelName';
import { chapterizationBaseText } from '@src/services/reel/reelChapterizationBaseText';
import { REEL_NAME_PROMPT_TITLE } from '@src/services/reel/reelNamingBaseText';
import { isReelPlaceholderTitle } from '@src/services/reel/reelPlaceholderTitle';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useReelNameGeneration } from './useReelNameGeneration';

import type { Timeline } from '@src/editor/models/Timeline';
import type { RenameClipService } from '@src/services/clip/RenameClipService';
import type { Reel } from '@src/services/reel/reelTypes';
import type { DatoClientStore } from '@src/stores-v2/DatoClientStore';

const logger = LogManager.getLogger('ReelEditor:useReelAutoName');

export type ReelAutoNameInput = {
  clipId?: string;
  /** The reel's stored title — where the displayed name starts from. */
  clipTitle: string;
  storyId?: string;
  storyTitle?: string;
  showcaseId?: string;
  /** The reel as it plays; absent until the renderer has built it. */
  timeline?: Timeline;
  /** Section names and chapter titles, for the headings the model reads. */
  reel?: Reel;
  /**
   * False for a clip that is not a reel and with `enableBasicReelCreation` off:
   * no automatic run, and no Generate Name in the title row (DS9).
   */
  enabled: boolean;
  datoClientStore?: DatoClientStore;
  renameClipService?: RenameClipService;
  /**
   * Puts the new title where the clip list reads it. Called before the write
   * and again after it, so it must be safe to repeat.
   */
  onRenamed: (title: string) => void;
};

export type ReelAutoName = {
  /** The reel's name as it now stands — what the title row shows. */
  name: string;
  /** An explicit ask, which always replaces what is there (DS5). */
  generate: () => void;
  /** A name the creator typed. An emptied field reverts (DS8). */
  rename: (name: string) => void;
  isGenerating: boolean;
  /** The last failure, which stands until the next run resolves it. */
  error?: string;
};

export function useReelAutoName(input: ReelAutoNameInput): ReelAutoName {
  const {
    clipId,
    clipTitle,
    storyId,
    storyTitle,
    showcaseId,
    timeline,
    reel,
    enabled,
    datoClientStore,
    renameClipService,
    onRenamed,
  } = input;

  const [name, setName] = useState(clipTitle);
  // Re-seeded on a clip switch, not on every `clipTitle` change: after a write
  // this hook's own `name` is ahead of the prop for as long as it takes the
  // host's in-memory update to come back around, and following the prop would
  // flash the old title.
  const seededForRef = useRef(clipId);
  if (seededForRef.current !== clipId) {
    seededForRef.current = clipId;
    setName(clipTitle);
  }

  const {
    generate: runPrompt,
    generatingIds,
    errors,
    lastError,
  } = useReelNameGeneration(
    REEL_NAME_PROMPT_TITLE,
    useMemo(
      () => ({ storyId, showcaseId, clipId }),
      [storyId, showcaseId, clipId],
    ),
    datoClientStore,
  );

  // Everything the run reads, mirrored so `runNaming` keeps one identity: it is
  // called from an effect that must fire once per reel, and a callback that
  // changed with the timeline would re-fire it on every edit.
  const latest = useRef({ timeline, reel, storyId, renameClipService, name });
  latest.current = { timeline, reel, storyId, renameClipService, name };

  const [writeError, setWriteError] = useState<string | undefined>();

  // The newest write per clip: only that one may settle the name, so a late
  // result from an older write cannot overwrite a newer one.
  const writeSeqRef = useRef(0);
  const latestWriteRef = useRef(new Map<string, number>());

  /**
   * Shows the title and puts it where the clip list reads it, then writes it;
   * a failed write puts the previous title back in both places, and a
   * successful one hands the title to `onRenamed` once more.
   */
  const write = useCallback(
    async (clip: string, next: string): Promise<boolean> => {
      const service = latest.current.renameClipService;
      if (!service) {
        setWriteError('The reel could not be renamed in this context');
        return false;
      }
      // Shown before the write, not after it: the round trip is long enough
      // that committing on Enter left the old title on screen until it landed
      // (PLAT-11503). Restored below if the write fails, so a failure still
      // ends with the name the clip actually has.
      const previous = latest.current.name;
      const seq = ++writeSeqRef.current;
      latestWriteRef.current.set(clip, seq);
      setName(next);
      onRenamed(next);

      const result = await service.rename(clip, next);
      if (latestWriteRef.current.get(clip) !== seq) return result.ok;
      // `name` and `writeError` belong to whichever clip is open now;
      // `onRenamed` is the host's callback for `clip`, so it still applies.
      const stillOpen = seededForRef.current === clip;
      if (!result.ok) {
        if (stillOpen) {
          setWriteError(result.error.message);
          setName(previous);
        }
        onRenamed(previous);
        return false;
      }
      if (stillOpen) setWriteError(undefined);
      // Again once it has landed: the host clears the preview cache here, and
      // a reopen during the write would have re-cached the old title.
      onRenamed(next);
      return true;
    },
    [onRenamed],
  );

  const runNaming = useCallback(
    async (source: 'creation' | 'manual') => {
      const {
        timeline: currentTimeline,
        reel: currentReel,
        storyId: currentStoryId,
      } = latest.current;
      if (!clipId || !currentTimeline) return;
      // A new run is the only thing that resolves the last one's failure — the
      // generation hook clears its own the same way.
      setWriteError(undefined);

      const sections = deriveReelSections(currentTimeline);
      const { text } = chapterizationBaseText(
        currentTimeline,
        sections,
        currentReel?.sections,
        currentReel?.chapters,
      );
      if (!text.trim()) {
        // Every Section is off or emptied. Nothing to characterise, and the
        // title stands — this is not a failure to report (manual step 10).
        logger.warn('Declining to name a reel with no words to read', {
          clipId,
        });
        return;
      }

      // Any write that starts while the model answers is a name the creator
      // chose after this run began — newer than an automatic run (DS2) or an
      // earlier ask (DS5) — so it stands.
      const lastWriteBefore = latestWriteRef.current.get(clipId);
      const raw = await runPrompt(
        clipId,
        text,
        // What the reel already calls its own parts, so the whole is not named
        // after one of them.
        reelNameAvoidanceInstruction(reelNamesInUse(currentReel)),
      );
      // The prompt forbids quotes, colons and over-length names; this is what
      // makes that true of the clip title it becomes.
      const generated = raw ? normalizeGeneratedReelName(raw) : null;
      if (!generated) {
        // `runPrompt` also answers `null` for a run it declined to start — a
        // generation for this reel is already in flight. Nothing failed there,
        // and counting it as a failure would inflate the rate with the creator
        // pressing Generate Name while the automatic run is still going.
        const message =
          lastError(clipId) ??
          (raw ? 'The generated name was not usable' : undefined);
        if (message) {
          EventTracker.trackReelNameGenerationFailed({
            clip_id: clipId,
            story_id: currentStoryId,
            source,
            error_message: message,
          });
        }
        return;
      }
      // Dropped rather than failed, so neither event reports it.
      if (latestWriteRef.current.get(clipId) !== lastWriteBefore) return;

      // E9 — a failure leaves the title as it was: a failed generation never
      // reaches `write`, and a failed write restores the previous name.
      if (!(await write(clipId, generated))) return;

      EventTracker.trackReelNameGenerated({
        clip_id: clipId,
        story_id: currentStoryId,
        source,
        section_count: sections.length,
        name_length: generated.length,
      });
    },
    [clipId, runPrompt, lastError, write],
  );

  /**
   * DS3 / DS4 — the automatic run, once per reel per session.
   *
   * Keyed by clip id in a ref rather than by state so a re-render, a tab switch
   * or a second modal instance cannot fire it twice; a failure marks the reel as
   * attempted too, so a broken prompt does not retry on every render.
   */
  const attemptedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!enabled || !clipId || !timeline) return;
    if (attemptedRef.current === clipId) return;
    // The title *is* the provenance (DS2): once anything else is there, someone
    // has named this reel and the automatic run stands down for good. Read
    // through the ref rather than the `name` binding: it changes as the run
    // lands, and it is not a reason to reconsider whether to run.
    if (!isReelPlaceholderTitle(latest.current.name, storyTitle)) return;

    attemptedRef.current = clipId;
    void runNaming('creation').catch(error => {
      logger.error('The automatic reel naming run threw', {
        clipId,
        ...extractErrorInfo(error),
      });
    });
  }, [enabled, clipId, timeline, storyTitle, runNaming]);

  const generate = useCallback(() => {
    // DS5 — an explicit request is consent, so this replaces whatever is there,
    // including a name the creator typed. The automatic run cannot start once
    // anything else is there, and either run stands down for a name typed
    // while it is generating.
    void runNaming('manual').catch(error => {
      logger.error('The requested reel naming run threw', {
        clipId,
        ...extractErrorInfo(error),
      });
    });
  }, [runNaming, clipId]);

  const rename = useCallback(
    (next: string) => {
      const trimmed = next.trim();
      // DS8 — a clip record cannot be nameless: the clip list has nothing to
      // render for one. An emptied field reverts to what was there rather than
      // `InlineRenameField` growing a mode for this one caller.
      if (!trimmed || !clipId || trimmed === latest.current.name) return;

      EventTracker.trackReelRenamed({
        clip_id: clipId,
        was_placeholder: isReelPlaceholderTitle(
          latest.current.name,
          storyTitle,
        ),
        name_length: trimmed.length,
      });
      void write(clipId, trimmed).catch(error => {
        logger.error('The reel rename threw', {
          clipId,
          ...extractErrorInfo(error),
        });
      });
    },
    [clipId, storyTitle, write],
  );

  return {
    name,
    generate,
    rename,
    isGenerating: !!clipId && generatingIds.has(clipId),
    error: writeError ?? (clipId ? errors[clipId] : undefined),
  };
}
